import type { CustomerCatalog, Locale, StoreSummary } from "./domain";

const DATABASE = "gobro-customer-v1";
const STORE = "catalog-cache";

export interface CachedValue<T> {
  cachedAt: string;
  value: T;
}

export interface CustomerCache {
  getCatalog(slug: string, locale: Locale): Promise<CachedValue<CustomerCatalog> | null>;
  getStores(locale: Locale): Promise<CachedValue<StoreSummary[]> | null>;
  putCatalog(slug: string, locale: Locale, catalog: CustomerCatalog): Promise<void>;
  putStores(locale: Locale, stores: StoreSummary[]): Promise<void>;
}

export class IndexedDbCustomerCache implements CustomerCache {
  getCatalog(slug: string, locale: Locale) {
    return this.get<CachedValue<CustomerCatalog>>(`catalog:${locale}:${slug}`);
  }

  getStores(locale: Locale) {
    return this.get<CachedValue<StoreSummary[]>>(`stores:${locale}`);
  }

  putCatalog(slug: string, locale: Locale, catalog: CustomerCatalog) {
    return this.put(`catalog:${locale}:${slug}`, {
      cachedAt: new Date().toISOString(),
      value: catalog,
    });
  }

  putStores(locale: Locale, stores: StoreSummary[]) {
    return this.put(`stores:${locale}`, { cachedAt: new Date().toISOString(), value: stores });
  }

  private async get<T>(key: string): Promise<T | null> {
    const database = await openDatabase();
    return new Promise((resolve, reject) => {
      const request = database.transaction(STORE, "readonly").objectStore(STORE).get(key);
      request.onsuccess = () => resolve((request.result as T | undefined) ?? null);
      request.onerror = () => reject(request.error);
    });
  }

  private async put(key: string, value: unknown): Promise<void> {
    const database = await openDatabase();
    return new Promise((resolve, reject) => {
      const transaction = database.transaction(STORE, "readwrite");
      transaction.objectStore(STORE).put(value, key);
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
    });
  }
}

export class MemoryCustomerCache implements CustomerCache {
  private readonly values = new Map<string, CachedValue<unknown>>();
  async getCatalog(slug: string, locale: Locale) {
    return (this.values.get(`catalog:${locale}:${slug}`) as CachedValue<CustomerCatalog>) ?? null;
  }
  async getStores(locale: Locale) {
    return (this.values.get(`stores:${locale}`) as CachedValue<StoreSummary[]>) ?? null;
  }
  async putCatalog(slug: string, locale: Locale, catalog: CustomerCatalog) {
    this.values.set(`catalog:${locale}:${slug}`, {
      cachedAt: new Date().toISOString(),
      value: catalog,
    });
  }
  async putStores(locale: Locale, stores: StoreSummary[]) {
    this.values.set(`stores:${locale}`, { cachedAt: new Date().toISOString(), value: stores });
  }
}

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DATABASE, 1);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(STORE)) request.result.createObjectStore(STORE);
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}
